"use client"

// PortalMain.js - SIMPLIFICADO PARA REDIRECCIÓN DIRECTA

import { useState, useEffect, useCallback, useMemo } from "react"
import Layout from "./components/Layout"
import DesktopView from "./views/DesktopView"
import CalendarView from "./views/CalendarView"
import ProfileModal from "./modals/ProfileModal"
import NotificationsModal from "./modals/NotificationsModal"
import TaskModal from "./modals/TaskModal"
import LogoutModal from "./modals/LogoutModal"
import ResponsiveModalWrapper from "./modals/ResponsiveModalWrapper"
import FileLibraryContent from "./components/FileLibraryContent"
import HelpSupportContent from "./components/HelpSupportContent"
import { toDateKey } from "./utils/date"

import {  CheckSquare, Sun, Moon, Sunrise, Sunset } from "lucide-react"

const PortalFondoCafetero = ({
  user,
  onLogout,
  applications = [],
}) => {
  // API BASE URL
  const API_BASE_URL = useMemo(() => {
    return process.env.REACT_APP_API_URL
  }, [])

  // Estados principales
  const [activeTab, setActiveTab] = useState("desktop")
  const [backgroundMode, setBackgroundMode] = useState("dynamic")
  const [loading, setLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Estados para modales
  const [showNotifications, setShowNotifications] = useState(false)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [showEditTaskModal, setShowEditTaskModal] = useState(false)

  // Estados para datos
  const [tasks, setTasks] = useState([])
  const [newTask, setNewTask] = useState("")
  const [editingTask, setEditingTask] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [selectedDate, setSelectedDate] = useState(new Date())

  // Headers comunes para las peticiones
  const getHeaders = useCallback(() => {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${user?.token}`,
    }
  }, [user?.token])

  // Actualizar tiempo cada minuto con timezone de Honduras
  useEffect(() => {
    const updateTime = () => {
      const hondurasTime = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Tegucigalpa" }))
      setCurrentTime(hondurasTime)
    }
    updateTime()
    const timer = setInterval(updateTime, 1000)
    return () => clearInterval(timer)
  }, [])

  // Funciones de utilidad para tiempo y saludos
  const getTimeIcon = () => {
    const hour = currentTime.getHours()
    if (hour >= 6 && hour < 12) return <Sunrise className="h-5 w-5" style={{ color: '#7B5B3E' }} />
    if (hour >= 12 && hour < 18) return <Sun className="h-5 w-5" style={{ color: '#7B5B3E' }} />
    if (hour >= 18 && hour < 22) return <Sunset className="h-5 w-5" style={{ color: '#7B5B3E' }} />
    return <Moon className="h-5 w-5 text-blue-400" />
  }

  const getGreeting = () => {
    const hour = currentTime.getHours()

    if (hour >= 5 && hour < 12) {
      return "Buenos días"
    } else if (hour >= 12 && hour < 18) {
      return "Buenas tardes"
    } else if (hour >= 18 && hour < 22) {
      return "Buenas tardes"
    } else {
      return "Buenas noches"
    }
  }

  // Funciones auxiliares de mapeo
  const mapearTipoNotificacion = (tipoApi) => {
    const mapeo = {
      Info: "info",
      Información: "info",
      Advertencia: "warning",
      Warning: "warning",
      Error: "error",
      Éxito: "success",
      Success: "success",
    }
    return mapeo[tipoApi] || "info"
  }

  const mapearPrioridadTarea = (prioridadApi) => {
    const mapeo = { Alta: "high", Media: "medium", Baja: "low" }
    return mapeo[prioridadApi] || "medium"
  }

  const mapearEstadoTarea = (estadoApi) => {
    const mapeo = { Pendiente: "pending", "En Progreso": "inProgress", Realizada: "completed" }
    return mapeo[estadoApi] || "pending"
  }

  const isUserTask = useCallback(
    (task) => {
      if (!user) return false
      return (
        task.ownerId === user.id ||
        task.ownerId === Number.parseInt(user.id) ||
        task.createdBy === user.usuario ||
        task.createdBy === user.empleado?.NombreEmpleado ||
        task.userId === user.id ||
        task.userId === user.usuario
      )
    },
    [user],
  )

  // Cargar notificaciones
  const loadNotifications = useCallback(async () => {
    if (!API_BASE_URL || !user?.token) return

    try {
      const response = await fetch(`${API_BASE_URL}/notificaciones`, {
        method: "GET",
        headers: getHeaders(),
      })

      if (!response.ok) throw new Error(`Error al obtener notificaciones: ${response.status}`)

      const data = await response.json()
      const notificacionesTransformadas = data.data.map((notif) => ({
        id: notif.Id,
        type: mapearTipoNotificacion(notif.Tipo),
        title: notif.Titulo,
        message: notif.Mensaje,
        timestamp: notif.createdAt || new Date().toISOString(),
        read: notif.Estado === "Leido",
        service: notif.Titulo.includes("SAP") ? "SAP" : "Sistema",
      }))

      setNotifications(notificacionesTransformadas)
    } catch (error) {
      console.error("Error al cargar notificaciones:", error)
      setNotifications([
        {
          id: Date.now(),
          type: "error",
          title: "Error de Conexión",
          message: "No se pudieron cargar las notificaciones.",
          service: "Sistema",
          timestamp: new Date().toISOString(),
          read: false,
        },
      ])
    }
  }, [API_BASE_URL, user?.token, getHeaders])

  // Cargar tareas
  const loadTasks = useCallback(async () => {
    if (!API_BASE_URL || !user?.token) {
      try {
        const savedTasks = localStorage.getItem("userTasks")
        const allTasks = savedTasks ? JSON.parse(savedTasks) : []
        const userTasks = allTasks.filter(
          (task) =>
            task.userId === user?.id ||
            task.userId === user?.usuario ||
            task.createdBy === user?.usuario ||
            task.createdBy === user?.empleado?.NombreEmpleado,
        )
        setTasks(userTasks)
      } catch {
        setTasks([])
      }
      return
    }

    try {
      const userId = user?.id || user?.usuario
      const url = `${API_BASE_URL}/tareasEmpleados${userId ? `?usuarioId=${userId}` : ""}`
      const response = await fetch(url, { method: "GET", headers: getHeaders() })

      if (!response.ok) throw new Error(`Error al obtener tareas: ${response.status}`)

      const data = await response.json()
      const tareasFiltradas = data.data.filter(
        (tarea) =>
          tarea.UsuarioFK === user?.id ||
          tarea.UsuarioFK === Number.parseInt(user?.id) ||
          tarea.CreadoPor === user?.usuario ||
          tarea.CreadoPor === user?.empleado?.NombreEmpleado,
      )

      const tareasTransformadas = tareasFiltradas.map((tarea) => {
        let fechaISO = tarea.Fecha
        if (tarea.Fecha && typeof tarea.Fecha === "string") {
          const partesFecha = tarea.Fecha.split("-")
          if (partesFecha.length === 3) {
            fechaISO = `${partesFecha[2]}-${partesFecha[1]}-${partesFecha[0]}`
          }
        }

        return {
          id: tarea.Id,
          text: tarea.Tarea,
          date: fechaISO,
          completed: tarea.Estado === "Realizada",
          priority: mapearPrioridadTarea(tarea.Prioridad),
          status: mapearEstadoTarea(tarea.Estado),
          createdAt: tarea.FechaCreacion || new Date().toISOString(),
          employeeName: tarea.EmpleadoAsignado || tarea.CreadoPor || "Sin asignar",
          ownerId: tarea.UsuarioFK,
          createdBy: tarea.CreadoPor,
          userId: tarea.UsuarioFK,
        }
      })

      setTasks(tareasTransformadas)
    } catch (error) {
      console.error("Error al cargar tareas:", error)
      try {
        const savedTasks = localStorage.getItem("userTasks")
        const allTasks = savedTasks ? JSON.parse(savedTasks) : []
        const userTasks = allTasks.filter(
          (task) =>
            task.userId === user?.id ||
            task.userId === user?.usuario ||
            task.createdBy === user?.usuario ||
            task.createdBy === user?.empleado?.NombreEmpleado,
        )
        setTasks(userTasks)
      } catch {
        setTasks([])
      }
    }
  }, [API_BASE_URL, user?.token, user?.id, user?.usuario, user?.empleado?.NombreEmpleado, getHeaders])

  // Función para refrescar datos
  const handleRefresh = useCallback(async () => {
    setLoading(true)
    try {
      await Promise.all([loadNotifications(), loadTasks()])
    } catch (error) {
      console.error("Error al refrescar datos:", error)
    } finally {
      setLoading(false)
    }
  }, [loadNotifications, loadTasks])

  // FUNCIÓN PARA ACCEDER A APLICACIONES - REDIRECCIÓN DIRECTA
  const accessApplication = async (app, e) => {
    e.preventDefault()

    if (!app.Link || app.Link === "#" || app.Link === "#placeholder") {
      alert("Esta aplicación no tiene enlace configurado. Contacte al administrador del sistema.")
      return
    }

    if (!user || !user.token) {
      alert("Su sesión ha expirado. Por favor, inicie sesión nuevamente.")
      if (onLogout) onLogout()
      return
    }

    if (!app.Id && !app.id) {
      alert("Error: La aplicación no tiene un ID válido.")
      return
    }

    console.log("Accediendo a aplicación:", {
      name: app.NombreAplicacion || app.name,
      id: app.Id || app.id,
      link: app.Link,
    })

    // REDIRECCIÓN DIRECTA AL SUBDOMINIO
    console.log("Redirigiendo directamente a:", app.Link)
    window.location.href = app.Link
  }

  // Funciones de tareas (mantengo las existentes...)
  const addTask = async () => {
    if (!newTask.trim()) return

    const taskData = {
      text: newTask,
      date: toDateKey(selectedDate),
      completed: false,
      priority: "medium",
      status: "pending",
      createdAt: new Date().toISOString(),
      employeeName: user?.empleado?.NombreEmpleado || user?.usuario || "Usuario",
      ownerId: user?.id,
      createdBy: user?.usuario,
      userId: user?.id || user?.usuario,
    }

    if (API_BASE_URL && user?.token) {
      try {
        const response = await fetch(`${API_BASE_URL}/tareasEmpleados`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({
            Tarea: newTask,
            Fecha: toDateKey(selectedDate),
            Prioridad: "Media",
            Estado: "Pendiente",
            UsuarioFK: user.id,
            CreadoPor: user.usuario,
          }),
        })

        if (response.ok) {
          const data = await response.json()
          const newTaskWithId = { ...taskData, id: data.data.Id }
          setTasks((prev) => [...prev, newTaskWithId])
        } else {
          throw new Error("Error al crear tarea en el servidor")
        }
      } catch (error) {
        console.error("Error al agregar tarea:", error)
        const localTask = { ...taskData, id: Date.now() }
        setTasks((prev) => [...prev, localTask])

        try {
          const savedTasks = localStorage.getItem("userTasks")
          const allTasks = savedTasks ? JSON.parse(savedTasks) : []
          allTasks.push(localTask)
          localStorage.setItem("userTasks", JSON.stringify(allTasks))
        } catch (storageError) {
          console.error("Error al guardar en localStorage:", storageError)
        }
      }
    } else {
      const localTask = { ...taskData, id: Date.now() }
      setTasks((prev) => [...prev, localTask])

      try {
        const savedTasks = localStorage.getItem("userTasks")
        const allTasks = savedTasks ? JSON.parse(savedTasks) : []
        allTasks.push(localTask)
        localStorage.setItem("userTasks", JSON.stringify(allTasks))
      } catch (storageError) {
        console.error("Error al guardar en localStorage:", storageError)
      }
    }

    setNewTask("")
    setShowTaskModal(false)
  }

  const editTask = (task) => {
    setEditingTask({ id: task.id, text: task.text, date: task.date, priority: task.priority })
    setShowEditTaskModal(true)
  }

  const updateTask = async () => {
    if (!editingTask || !editingTask.text.trim()) return

    const updatedTask = {
      ...tasks.find((t) => t.id === editingTask.id),
      text: editingTask.text,
      date: editingTask.date,
      priority: editingTask.priority,
    }

    if (API_BASE_URL && user?.token) {
      try {
        const response = await fetch(`${API_BASE_URL}/tareasEmpleados/${editingTask.id}`, {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify({
            Tarea: editingTask.text,
            Fecha: editingTask.date,
            Prioridad: editingTask.priority === "high" ? "Alta" : editingTask.priority === "low" ? "Baja" : "Media",
          }),
        })

        if (response.ok) {
          setTasks((prev) => prev.map((task) => (task.id === editingTask.id ? updatedTask : task)))
        } else {
          throw new Error("Error al actualizar tarea en el servidor")
        }
      } catch (error) {
        console.error("Error al actualizar tarea:", error)
        setTasks((prev) => prev.map((task) => (task.id === editingTask.id ? updatedTask : task)))
      }
    } else {
      setTasks((prev) => prev.map((task) => (task.id === editingTask.id ? updatedTask : task)))

      try {
        const savedTasks = localStorage.getItem("userTasks")
        const allTasks = savedTasks ? JSON.parse(savedTasks) : []
        const updatedTasks = allTasks.map((task) => (task.id === editingTask.id ? updatedTask : task))
        localStorage.setItem("userTasks", JSON.stringify(updatedTasks))
      } catch (storageError) {
        console.error("Error al actualizar en localStorage:", storageError)
      }
    }

    setShowEditTaskModal(false)
    setEditingTask(null)
  }

  const changeTaskStatus = async (taskId, newStatus) => {
    const task = tasks.find((t) => t.id === taskId)
    if (!task) return

    const updatedTask = {
      ...task,
      status: newStatus,
      completed: newStatus === "completed",
    }

    if (API_BASE_URL && user?.token) {
      try {
        const estadoApi =
          newStatus === "completed" ? "Realizada" : newStatus === "inProgress" ? "En Progreso" : "Pendiente"
        const response = await fetch(`${API_BASE_URL}/tareasEmpleados/${taskId}`, {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify({ Estado: estadoApi }),
        })

        if (response.ok) {
          setTasks((prev) => prev.map((t) => (t.id === taskId ? updatedTask : t)))
        } else {
          throw new Error("Error al actualizar estado en el servidor")
        }
      } catch (error) {
        console.error("Error al cambiar estado de tarea:", error)
        setTasks((prev) => prev.map((t) => (t.id === taskId ? updatedTask : t)))
      }
    } else {
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updatedTask : t)))

      try {
        const savedTasks = localStorage.getItem("userTasks")
        const allTasks = savedTasks ? JSON.parse(savedTasks) : []
        const updatedTasks = allTasks.map((t) => (t.id === taskId ? updatedTask : t))
        localStorage.setItem("userTasks", JSON.stringify(updatedTasks))
      } catch (storageError) {
        console.error("Error al actualizar en localStorage:", storageError)
      }
    }
  }

  const toggleTask = async (taskId) => {
    const task = tasks.find((t) => t.id === taskId)
    if (!task) return
    const newStatus = task.completed ? "pending" : "completed"
    changeTaskStatus(taskId, newStatus)
  }

  const deleteTask = async (taskId) => {
    if (API_BASE_URL && user?.token) {
      try {
        const response = await fetch(`${API_BASE_URL}/tareasEmpleados/${taskId}`, {
          method: "DELETE",
          headers: getHeaders(),
        })

        if (response.ok) {
          setTasks((prev) => prev.filter((task) => task.id !== taskId))
        } else {
          throw new Error("Error al eliminar tarea en el servidor")
        }
      } catch (error) {
        console.error("Error al eliminar tarea:", error)
        setTasks((prev) => prev.filter((task) => task.id !== taskId))
      }
    } else {
      setTasks((prev) => prev.filter((task) => task.id !== taskId))

      try {
        const savedTasks = localStorage.getItem("userTasks")
        const allTasks = savedTasks ? JSON.parse(savedTasks) : []
        const filteredTasks = allTasks.filter((task) => task.id !== taskId)
        localStorage.setItem("userTasks", JSON.stringify(filteredTasks))
      } catch (storageError) {
        console.error("Error al eliminar de localStorage:", storageError)
      }
    }
  }

  const getTodayTasks = () => {
    const today = toDateKey(new Date())
    return tasks.filter((task) => task.date === today && isUserTask(task))
  }

  // Funciones de notificaciones
  const clearAllNotifications = async () => {
    if (API_BASE_URL && user?.token) {
      try {
        await fetch(`${API_BASE_URL}/notificaciones/marcar-todas-leidas`, {
          method: "PUT",
          headers: getHeaders(),
        })
        setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })))
      } catch (error) {
        console.error("Error al marcar notificaciones como leídas:", error)
        setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })))
      }
    } else {
      setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })))
    }
  }

  const markAsRead = async (notificationId) => {
    if (API_BASE_URL && user?.token) {
      try {
        await fetch(`${API_BASE_URL}/notificaciones/${notificationId}/marcar-leida`, {
          method: "PUT",
          headers: getHeaders(),
        })
        setNotifications((prev) =>
          prev.map((notif) => (notif.id === notificationId ? { ...notif, read: true } : notif)),
        )
      } catch (error) {
        console.error("Error al marcar notificación como leída:", error)
        setNotifications((prev) =>
          prev.map((notif) => (notif.id === notificationId ? { ...notif, read: true } : notif)),
        )
      }
    } else {
      setNotifications((prev) => prev.map((notif) => (notif.id === notificationId ? { ...notif, read: true } : notif)))
    }
  }

  // Cargar datos iniciales
  useEffect(() => {
    if (user) {
      loadNotifications()
      loadTasks()
    }
  }, [user, loadNotifications, loadTasks])

  return (
    <Layout
      user={user}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      onRefresh={handleRefresh}
      loading={loading}
      backgroundMode={backgroundMode}
      setBackgroundMode={setBackgroundMode}
      notifications={notifications}
      onShowNotifications={() => setShowNotifications(true)}
      onShowFileLibrary={() => setActiveTab("files")}
      onShowProfile={() => setShowProfile(true)}
      onShowLogout={() => setShowLogoutConfirm(true)}
      onShowHelpSupport={() => setActiveTab("help")}
    >
      {/* Contenido según la pestaña activa */}
      {activeTab === "desktop" && (
        <DesktopView
          user={user}
          currentTime={currentTime}
          getTimeIcon={getTimeIcon}
          getGreeting={getGreeting}
          applications={applications}
          getTodayTasks={getTodayTasks}
          toggleTask={toggleTask}
          editTask={editTask}
          accessApplication={accessApplication}
          notifications={notifications}
          onShowNotifications={() => setShowNotifications(true)}
        />
      )}

      {activeTab === "calendar" && (
        <CalendarView
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          setShowTaskModal={setShowTaskModal}
          tasks={tasks}
          toggleTask={toggleTask}
          deleteTask={deleteTask}
          editTask={editTask}
          changeTaskStatus={changeTaskStatus}
        />
      )}

      {activeTab === "files" && <FileLibraryContent user={user} API_BASE_URL={API_BASE_URL} getHeaders={getHeaders} />}

      {activeTab === "help" && <HelpSupportContent applications={applications} user={user} />}

      {/* Modales */}
      <ProfileModal show={showProfile} onClose={() => setShowProfile(false)} user={user} />

      <NotificationsModal
        show={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        onClearAll={clearAllNotifications}
        onMarkAsRead={markAsRead}
      />

      <TaskModal
        show={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        newTask={newTask}
        setNewTask={setNewTask}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        addTask={addTask}
      />

      <ResponsiveModalWrapper
        show={showEditTaskModal}
        onClose={() => {
          setShowEditTaskModal(false)
          setEditingTask(null)
        }}
        title="Editar Tarea"
        icon={<CheckSquare className="h-6 w-6" />}
        maxWidth="max-w-md"
        customHeaderBg="#7B5B3E"
      >
        {editingTask && (
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                <input
                  type="text"
                  value={editingTask.text}
                  onChange={(e) => setEditingTask({ ...editingTask, text: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent"
                  placeholder="¿Qué necesitas hacer?"
                  style={{ focusRingColor: '#7B5B3E' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha</label>
                <input
                  type="date"
                  value={editingTask.date}
                  onChange={(e) => setEditingTask({ ...editingTask, date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: '#7B5B3E' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                <select
                  value={editingTask.priority}
                  onChange={(e) => setEditingTask({ ...editingTask, priority: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: '#7B5B3E' }}
                >
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowEditTaskModal(false)
                  setEditingTask(null)
                }}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={updateTask}
                className="flex-1 px-6 py-3 text-white rounded-xl transition-all font-semibold"
                style={{ backgroundColor: '#7B5B3E' }}
              >
                Actualizar Tarea
              </button>
            </div>
          </div>
        )}
      </ResponsiveModalWrapper>

      <LogoutModal
        show={showLogoutConfirm}
        user={user}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={async () => {
          // El modal se queda abierto ("Cerrando sesión…") hasta que el cierre termina y el portal se desmonta
          if (onLogout) {
            await onLogout()
          } else {
            setShowLogoutConfirm(false)
          }
        }}
      />
    </Layout>
  )
}

export default PortalFondoCafetero